import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckIcon, ShieldCheckIcon, CalendarIcon, DollarSignIcon, PlusIcon, StarIcon, ArrowLeftIcon } from 'lucide-react';
import { ProgressBar } from './ProgressBar';
import { User, MichiganLicenseData, RenewalEntry, MICHIGAN_STEPS, createDefaultMichiganLicense } from './types';
interface MichiganModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  license: MichiganLicenseData;
  onUpdateLicense: (license: MichiganLicenseData) => void;
}
export function MichiganModal({
  user,
  isOpen,
  onClose,
  license,
  onUpdateLicense
}: MichiganModalProps) {
  const completedCount = license.steps.filter(Boolean).length;
  const allComplete = completedCount === MICHIGAN_STEPS.length;
  const stepTotalCost = license.stepCosts.reduce((sum, cost) => sum + (cost || 0), 0);
  const renewalsTotalCost = license.renewals.reduce((sum, r) => sum + (r.fee || 0), 0);
  const totalCost = stepTotalCost + renewalsTotalCost;
  const [isAddingRenewal, setIsAddingRenewal] = useState(false);
  const [newRenewalYear, setNewRenewalYear] = useState<number>(new Date().getFullYear() + 1);
  const [newRenewalFee, setNewRenewalFee] = useState<number>(0);
  const [newRenewalDate, setNewRenewalDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const handleToggleStep = (index: number) => {
    const newSteps = [...license.steps];
    newSteps[index] = !newSteps[index];
    onUpdateLicense({
      ...license,
      steps: newSteps
    });
  };
  const handleStepCostChange = (index: number, cost: number) => {
    const newCosts = [...license.stepCosts];
    newCosts[index] = cost;
    onUpdateLicense({
      ...license,
      stepCosts: newCosts
    });
  };
  const handleStartDateChange = (date: string) => {
    onUpdateLicense({
      ...license,
      startDate: date
    });
  };
  const handleExpirationDateChange = (date: string) => {
    onUpdateLicense({
      ...license,
      expirationDate: date
    });
  };
  const handleSaveRenewal = () => {
    const newRenewals = [...license.renewals, {
      year: newRenewalYear,
      fee: newRenewalFee,
      date: newRenewalDate
    }].sort((a, b) => b.year - a.year);
    onUpdateLicense({
      ...license,
      renewals: newRenewals
    });
    setIsAddingRenewal(false);
    setNewRenewalYear(new Date().getFullYear() + 1);
    setNewRenewalFee(0);
    setNewRenewalDate(new Date().toISOString().split('T')[0]);
  };
  const handleRemoveRenewal = (index: number) => {
    const newRenewals = [...license.renewals];
    newRenewals.splice(index, 1);
    onUpdateLicense({
      ...license,
      renewals: newRenewals
    });
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-139" data-mp="249vreyty2g2o">
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-id="element-140" data-mp="24adv2gixrwkm">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-141" data-mp="2998p6emct7k1" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-2xl bg-slate-900 border border-amber-500/30 rounded-2xl shadow-[0_0_40px_rgba(245,158,11,0.1)] overflow-hidden flex flex-col max-h-[90vh]" data-id="element-142" data-mp="3jt3e3vdiz880">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/50" data-id="element-143" data-mp="ub338ql7cks1">
            <div className="flex items-center gap-3" data-id="element-144" data-mp="etcuydl6ztva">
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-145" data-mp="ers00muyu8ww">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-146" data-mp="4wgla4jk51p5" />
              </button>
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center" data-id="element-147" data-mp="9t72iv73m3c5">
                <StarIcon className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-148" data-mp="xkm6stifquq1" />
              </div>
              <div data-id="element-149" data-mp="12ixjrd411lch">
                <h2 className="text-lg font-bold text-white" data-id="element-150" data-mp="12i4hopg0jk0o">
                  Michigan License
                </h2>
                <p className="text-xs text-slate-500" data-id="element-151" data-mp="nrovp8frw81g">
                  {user.name} · #{user.nmlsId}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-152" data-mp="1gi6z6akesbfe">
              <AnimatePresence mode="wait" data-id="element-153" data-mp="1gglzk6j8m2ao">
                {allComplete ? <motion.div key="licensed" initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.8
              }} className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/15 border border-emerald-500/25 rounded-full" data-id="element-154" data-mp="1lagiv8uxkf98">
                    <ShieldCheckIcon className="w-3.5 h-3.5 text-emerald-400" data-id="element-155" data-mp="2i2x0hg1k435z" />
                    <span className="text-xs font-semibold text-emerald-400" data-id="element-156" data-mp="2w1mxywsag4uv">
                      Licensed
                    </span>
                  </motion.div> : <motion.div key="in-progress" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full" data-id="element-157" data-mp="bamgin9vlhcn">
                    <span className="text-xs font-semibold text-amber-400" data-id="element-158" data-mp="3a0dye0ocplyy">
                      In Progress
                    </span>
                  </motion.div>}
              </AnimatePresence>
              <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors" data-id="element-159" data-mp="2mce6uyj7trjb">
                <XIcon className="w-5 h-5" data-id="element-160" data-mp="2v4xy3dx6gtu4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-8" data-id="element-161" data-mp="1ynuxefofj8pn">
            {/* Progress & Cost */}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-162" data-mp="22nc41cflebwe">
              <div className="flex-1 max-w-md" data-id="element-163" data-mp="1v42w7nmqil9g">
                <ProgressBar current={completedCount} total={MICHIGAN_STEPS.length} colorClass="bg-amber-500" data-id="element-164" data-mp="1v2rs1mpdnbht" />
              </div>
              <div className="flex items-center gap-2 bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-2" data-id="element-165" data-mp="1m6acm79zcrg9">
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-166" data-mp="1c6tau0xumxpk">
                  Total Cost
                </span>
                <span className="text-lg font-bold text-white tabular-nums" data-id="element-167" data-mp="17e03r6ewrdab">
                  ${totalCost.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Start Date */}
            <div data-id="element-168" data-mp="ycsax3v5v4nm">
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-2" data-id="element-169" data-mp="tfsc9e0n546j">
                Sponsorship Start Date
              </label>
              <div className="flex items-center bg-slate-800/50 border border-slate-700 rounded-xl px-3 py-2.5 focus-within:border-amber-500/50 focus-within:ring-1 focus-within:ring-amber-500/50 transition-all max-w-xs" data-id="element-170" data-mp="ohh3l6m30zpw">
                <CalendarIcon className="w-4 h-4 text-slate-500 mr-2" data-id="element-171" data-mp="2ha3ywt15bxdq" />
                <input type="date" value={license.startDate || ''} onChange={e => handleStartDateChange(e.target.value)} className="w-full bg-transparent text-sm text-slate-200 focus:outline-none" style={{
                colorScheme: 'dark'
              }} data-id="element-172" data-mp="23c74a269611p" />
              </div>
            </div>

            {/* Checklist */}
            <div data-id="element-173" data-mp="1onj7xtol1nap">
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-3" data-id="element-174" data-mp="1on15ugf8oeuv">
                Licensing Steps
              </label>
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-2" data-id="element-175" data-mp="1aqdvnby9zs70">
                <ol className="space-y-1" data-id="element-176" data-mp="1fp7cnecru1bx">
                  {MICHIGAN_STEPS.map((stepLabel, index) => {
                  const isComplete = license.steps[index];
                  const cost = license.stepCosts[index];
                  return <li key={index} data-id="element-177" data-mp="160jrxl8wx1b7" data-mpi={index}>
                        <div className="w-full flex items-start gap-3.5 py-3 px-3 rounded-xl transition-colors duration-150 hover:bg-slate-800/60 group" data-id="element-178" data-mp="15xfio9glbfdo">
                          <button onClick={() => handleToggleStep(index)} className="relative flex-shrink-0 mt-0.5 focus:outline-none" aria-label={`${isComplete ? 'Uncheck' : 'Check'}: ${stepLabel}`} data-id="element-179" data-mp="1ez59bd2neaae">
                            <motion.div className={`w-7 h-7 rounded-lg flex items-center justify-center border-2 transition-colors duration-200 ${isComplete ? 'bg-emerald-500 border-emerald-500' : 'border-slate-600 group-hover:border-slate-500'}`} whileTap={{
                          scale: 0.9
                        }} data-id="element-180" data-mp="1js841oj5vps6">
                              <AnimatePresence mode="wait" data-id="element-181" data-mp="3twwj4j81hnxo">
                                {isComplete ? <motion.div key="check" initial={{
                              opacity: 0,
                              scale: 0
                            }} animate={{
                              opacity: 1,
                              scale: 1
                            }} exit={{
                              opacity: 0,
                              scale: 0
                            }} transition={{
                              type: 'spring',
                              stiffness: 500,
                              damping: 30
                            }} data-id="element-182" data-mp="3oxjzs8gdmyzu">
                                    <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-183" data-mp="1grrhfz9af8l5" />
                                  </motion.div> : <motion.span key="number" initial={{
                              opacity: 0
                            }} animate={{
                              opacity: 1
                            }} exit={{
                              opacity: 0
                            }} className="text-[11px] font-bold text-slate-500" data-id="element-184" data-mp="1vgnzmp5261zh">
                                    {index + 1}
                                  </motion.span>}
                              </AnimatePresence>
                            </motion.div>
                            {index < MICHIGAN_STEPS.length - 1 && <div className={`absolute left-1/2 -translate-x-1/2 top-full w-0.5 h-[calc(0.25rem+12px)] transition-colors duration-300 ${isComplete ? 'bg-emerald-500/40' : 'bg-slate-700/50'}`} data-id="element-185" data-mp="1wd2yzqhhznga" />}
                          </button>

                          <button onClick={() => handleToggleStep(index)} className="flex-1 pt-0.5 text-left focus:outline-none" data-id="element-186" data-mp="214uorystxp5d">
                            <span className={`text-sm leading-snug transition-all duration-200 ${isComplete ? 'text-slate-500 line-through decoration-slate-600' : 'text-slate-200 group-hover:text-white'}`} data-id="element-187" data-mp="2a2wli25j5vmu">
                              {stepLabel}
                            </span>
                          </button>

                          <div className="flex-shrink-0 flex items-center bg-slate-900/50 rounded-lg px-2.5 py-1.5 border border-slate-700/50 focus-within:border-amber-500/50 focus-within:bg-slate-900 transition-colors" data-id="element-188" data-mp="2oq50zf5vgn4m">
                            <DollarSignIcon className="w-3.5 h-3.5 text-slate-500 mr-1" data-id="element-189" data-mp="2ft4j7rdb5na5" />
                            <input type="number" min="0" value={cost === 0 ? '' : cost} placeholder="0" onChange={e => handleStepCostChange(index, parseInt(e.target.value) || 0)} className="w-14 bg-transparent text-sm text-slate-300 tabular-nums focus:outline-none placeholder:text-slate-600" aria-label={`Cost for ${stepLabel}`} data-id="element-190" data-mp="213yiu02qhr0o" />
                          </div>
                        </div>
                      </li>;
                })}
                </ol>
              </div>
            </div>

            {/* Expiration & Renewals */}
            <AnimatePresence data-id="element-191" data-mp="wadnhufh2nrf">
              {allComplete && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="pt-6 border-t border-slate-800 space-y-6" data-id="element-192" data-mp="rc3pzd69zb2n">
                  <div data-id="element-193" data-mp="2y75zvd0s2x96">
                    <label className="block text-xs font-medium text-emerald-400/80 uppercase tracking-wider mb-2" data-id="element-194" data-mp="2y6cofyqvjjkp">
                      License Expiration
                    </label>
                    <div className="flex items-center bg-emerald-500/[0.05] border border-emerald-500/20 rounded-xl px-3 py-2.5 focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/50 transition-all max-w-xs" data-id="element-195" data-mp="37xqs8ipuz5jn">
                      <CalendarIcon className="w-4 h-4 text-emerald-500/70 mr-2" data-id="element-196" data-mp="2k8eo3x9e1no7" />
                      <input type="date" value={license.expirationDate || ''} onChange={e => handleExpirationDateChange(e.target.value)} className="w-full bg-transparent text-sm text-slate-200 focus:outline-none" style={{
                    colorScheme: 'dark'
                  }} data-id="element-197" data-mp="2p81248x1j7y2" />
                    </div>
                  </div>

                  <div data-id="element-198" data-mp="3kw6pcd5mkrg9">
                    <div className="flex items-center justify-between mb-4" data-id="element-199" data-mp="3kwgig9lyveq6">
                      <label className="block text-xs font-medium text-blue-400/80 uppercase tracking-wider" data-id="element-200" data-mp="3fzgdy34rmk16">
                        Renewals
                      </label>
                      {!isAddingRenewal && <button onClick={() => setIsAddingRenewal(true)} className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors bg-blue-500/10 px-3 py-1.5 rounded-lg" data-id="element-201" data-mp="2nbaqh8mbugxn">
                          <PlusIcon className="w-3.5 h-3.5" data-id="element-202" data-mp="29gi3xq80s0hk" />
                          Add Renewal
                        </button>}
                    </div>

                    {license.renewals.length > 0 && <div className="space-y-2 mb-4" data-id="element-203" data-mp="ornsx91bkxc6">
                        {license.renewals.map((renewal, idx) => <div key={idx} className="flex items-center justify-between bg-slate-800/40 rounded-xl px-4 py-3 border border-slate-700/50 group" data-id="element-204" data-mp="a39qv0bq9cwo" data-mpi={idx}>
                            <div className="flex flex-col" data-id="element-205" data-mp="12ryouqhhl247">
                              <span className="text-sm text-slate-200 font-medium" data-id="element-206" data-mp="17qs0seh4kl29">
                                {renewal.year} Renewal
                              </span>
                              <span className="text-xs text-slate-500" data-id="element-207" data-mp="xscb8fxqxjuv">
                                Submitted: {renewal.date}
                              </span>
                            </div>
                            <div className="flex items-center gap-4" data-id="element-208" data-mp="3iil1dqtlgvsm">
                              <span className="text-sm text-slate-300 tabular-nums font-medium" data-id="element-209" data-mp="3iggqgoxa2wiu">
                                ${renewal.fee}
                              </span>
                              <button onClick={() => handleRemoveRenewal(idx)} className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all p-1" aria-label="Remove renewal" data-id="element-210" data-mp="903zqxby7yp5">
                                <XIcon className="w-4 h-4" data-id="element-211" data-mp="wt2pr1rpntc7" />
                              </button>
                            </div>
                          </div>)}
                      </div>}

                    <AnimatePresence data-id="element-212" data-mp="j0mnw4ajq1cv">
                      {isAddingRenewal && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-213" data-mp="1gfqwcbox3qpz">
                          <div className="bg-slate-800/60 rounded-xl border border-slate-700 p-4" data-id="element-214" data-mp="1x0r6cvd3x621">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4" data-id="element-215" data-mp="21wey9iiz4db8">
                              <div data-id="element-216" data-mp="26vrkc120tgcr">
                                <label className="block text-xs text-slate-400 mb-1.5" data-id="element-217" data-mp="2bocej3ou9vgt">
                                  Year
                                </label>
                                <input type="number" value={newRenewalYear} onChange={e => setNewRenewalYear(parseInt(e.target.value) || new Date().getFullYear())} className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50" data-id="element-218" data-mp="2gmm510zmc7w3" />
                              </div>
                              <div data-id="element-219" data-mp="dc35ysd6ys5k">
                                <label className="block text-xs text-slate-400 mb-1.5" data-id="element-220" data-mp="icqvtapop2y7">
                                  Fee
                                </label>
                                <div className="flex items-center bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 focus-within:border-blue-500/50" data-id="element-221" data-mp="n63hpd2zavn9">
                                  <DollarSignIcon className="w-4 h-4 text-slate-500 mr-1.5" data-id="element-222" data-mp="w601p1efb6bz" />
                                  <input type="number" min="0" value={newRenewalFee === 0 ? '' : newRenewalFee} placeholder="0" onChange={e => setNewRenewalFee(parseInt(e.target.value) || 0)} className="w-full bg-transparent text-sm text-slate-200 tabular-nums focus:outline-none" data-id="element-223" data-mp="114tgcx5g53dq" />
                                </div>
                              </div>
                              <div data-id="element-224" data-mp="1g4p7capju2a9">
                                <label className="block text-xs text-slate-400 mb-1.5" data-id="element-225" data-mp="2cp7p2hbj76is">
                                  Date Submitted
                                </label>
                                <input type="date" value={newRenewalDate} onChange={e => setNewRenewalDate(e.target.value)} className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50" style={{
                            colorScheme: 'dark'
                          }} data-id="element-226" data-mp="60fepbtpue73" />
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-3" data-id="element-227" data-mp="3efpbq5ir0t80">
                              <button onClick={() => setIsAddingRenewal(false)} className="text-sm text-slate-400 hover:text-slate-300 font-medium px-3 py-1.5" data-id="element-228" data-mp="17priofeszs3v">
                                Cancel
                              </button>
                              <button onClick={handleSaveRenewal} className="text-sm bg-blue-500 text-white px-4 py-1.5 rounded-lg hover:bg-blue-600 transition-colors font-medium shadow-sm" data-id="element-229" data-mp="2s8v1yq4r59eq">
                                Save Renewal
                              </button>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-end" data-id="element-230" data-mp="1vhj90q70zj5s">
            <button onClick={onClose} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-xl transition-colors" data-id="element-231" data-mp="1qig91k5wk34o">
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}